import { useCallback, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import type { CapturePhotoRow } from '../../data/database.types';
import { useProject } from '../../data/queries/use-project';
import { useCaptures } from '../../data/queries/use-captures';
import { useCompanyBranding } from '../../data/queries/use-company-branding';
import { useSession } from '../auth/session';
import { Link } from 'react-router-dom';
import { SETTINGS_PATH } from '../settings/settings-route';
import { useReportBuilder } from './report-builder-state';
import { reportBuilderPath, REPORT_VIEWER_STATE_KEY, viewerPhotoKey } from './report-route';
import { buildReportDocument, type ReportDocument } from './report-document';
import { ReportDocumentBody } from './ReportDocumentView';
import { useReportPhotos } from './use-report-photos';
import { usePinchZoom } from './use-pinch-zoom';
import { PhotoViewer } from '../photos/PhotoViewer';
import { viewerLabel } from './report-wording';
import { useCompanyLogoBytes } from './use-company-logo-bytes';
import { useReportPdf } from './use-report-pdf';
import { ReportPdfActions } from './ReportPdfActions';

/**
 * STEP THREE — the client-facing preview. M10 / S10-03B.
 *
 * **THIS IS A RESPONSIVE FULL-REPORT CONTENT PREVIEW, NOT A PICTURE OF A PDF.**
 * Jerry's decision, 2026-08-19, after physical Android testing. The question it
 * answers is **"is this all the content I am about to send the client?"** — so
 * every Capture, every chosen photo and every field must be readable and
 * reachable on a phone. Where the PDF will break pages is **S10-04's** question
 * about a document S10-04 will actually generate.
 *
 * **WHAT THIS REPLACED, AND WHY IT HAD TO GO.** The preview used to render an
 * 816px US Letter page and scale it down to fit the phone — about 0.45× on a
 * 390px screen. Two consequences, both fatal to the job above: **every word and
 * photo shrank with the page**, so ordinary report text could not be read
 * without pinching; and **a page-shaped box with a fixed height clipped
 * whatever ran past it**, which is why Jerry's screenshot showed the sheet
 * ending mid-Capture with the "PDF comes next" line stranded below an empty
 * band. Pagination on screen cannot be right, because the screen is not the
 * page.
 *
 * **THE CONTENT MODEL IS UNCHANGED AND SHARED.** `buildReportDocument` still
 * decides what the report contains; `ReportDocumentBody` still renders those
 * sections. Only the geometry around them changed — from a scaled print page to
 * ordinary responsive document flow. S10-04 renders the same model at print
 * dimensions.
 *
 * **NOTHING MEASURES ANYTHING HERE ANY MORE**, except the gesture surface. The
 * off-screen measuring strip existed only to feed the page packer, and the page
 * packer no longer runs on this screen.
 *
 * **THIS SCREEN STARTS NO UPLOADS.** It reads photos through a read-only seam
 * with no queue, no staging and no writers — see `use-report-photos.ts`.
 */
export function ReportPreviewStep() {
  const builder = useReportBuilder();
  const navigate = useNavigate();
  const location = useLocation();
  const { project } = useProject(builder.projectId);
  const { profile } = useSession();
  const { status: capturesStatus, captures } = useCaptures(builder.projectId);
  const { branding } = useCompanyBranding();
  const logo = useCompanyLogoBytes(builder.includeBranding ? branding : null);
  const selectedCaptures = captures.filter(({ capture }) => builder.captureIds.has(capture.id));
  const { byCapture } = useReportPhotos(selectedCaptures.map(({ capture }) => capture.id));

  /**
   * The document's own size, for the pan bounds ONLY.
   *
   * **NOTHING ABOUT THE REPORT DEPENDS ON THIS NUMBER** — it does not decide a
   * width, a break or a scale. It exists so that a zoomed-in drag knows how far
   * it may travel before the document would leave the window.
   *
   * Observed rather than read once, because the height grows as photographs
   * arrive. Where `ResizeObserver` is absent the single reading still gives
   * usable bounds, and pinch-zoom is a device gesture in any case.
   */
  const [content, setContent] = useState<{ width: number; height: number }>({
    width: 0,
    height: 0,
  });

  const measureContent = useCallback((node: HTMLElement | null) => {
    if (!node) return undefined;
    const read = () => {
      setContent({ width: node.clientWidth, height: node.scrollHeight });
    };
    read();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(read);
    observer.observe(node);
    // React 19 runs a ref callback's return value as its cleanup.
    return () => {
      observer.disconnect();
    };
  }, []);

  /** The window the report is looked at through, measured for pan bounds. */
  const [surface, setSurface] = useState<{ width: number; height: number } | null>(null);
  const measureSurface = useCallback((node: HTMLDivElement | null) => {
    if (node) setSurface({ width: node.clientWidth, height: node.clientHeight });
  }, []);

  /**
   * **THE HEADER NEVER PAINTS WITHOUT ITS DATE.** Dates are formatted in the
   * canonical Crewlink timezone, which arrives with the profile. A client-facing
   * report showing a blank date — even for one frame — is a defect, so the
   * document waits rather than guessing at the device's clock (INV-T17).
   */
  const timeZone = profile?.timezone ?? null;
  const waiting = timeZone === null || !project || capturesStatus === 'loading';

  const document: ReportDocument = buildReportDocument({
    includeBranding: builder.includeBranding,
    branding: branding
      ? {
          companyName: branding.company_name,
          logoMime: branding.logo_mime,
          logoBase64: logo,
          email: branding.contact_email,
          phone: branding.contact_phone,
        }
      : null,
    projectName: project?.name ?? '',
    timeZone: timeZone ?? 'UTC',
    now: new Date(),
    description: builder.description,
    captures: selectedCaptures.map(({ capture, organization }) => ({
      capture,
      locationDisplay: organization?.location_display ?? null,
      // **ONLY WHAT HE CHOSE.** Unselected evidence is filtered out here, so no
      // renderer downstream has the opportunity to show it.
      selected: (byCapture.get(capture.id) ?? []).filter((view) =>
        builder.photoKeysFor(capture.id).has(view.key),
      ),
    })),
  });

  /**
   * **THE GESTURE LAYER IS LAST, AND IT DECIDES NOTHING.** It is handed the
   * document's own rendered size — the size responsive CSS already chose — and
   * moves the picture of it around a window. It cannot change a width, a
   * typeface or what the report contains.
   */
  const zoom = usePinchZoom(
    content.width,
    content.height,
    surface?.width ?? 0,
    surface?.height ?? 0,
  );

  /**
   * The server row behind every selected photo.
   *
   * **THE SAME READ-ONLY SEAM THE PREVIEW USES.** `useReportPhotos` is one
   * batched query with no queue, no staging and no writers; the PDF takes the
   * rows it already returned rather than opening a second path to the photos.
   */
  const photoRows = new Map<string, CapturePhotoRow>();
  for (const views of byCapture.values()) {
    for (const view of views) {
      if (view.row) photoRows.set(view.key, view.row);
    }
  }

  const pdf = useReportPdf({ document, rows: photoRows, timeZone, projectId: builder.projectId });

  const openKey = viewerPhotoKey(location.state);
  const allViews = [...byCapture.values()].flat();
  const viewed = openKey ? (allViews.find((view) => view.key === openKey) ?? null) : null;

  const inspect = (key: string): void => {
    // A history entry, so Android's Back closes the photo and leaves Brandon
    // on the preview with his zoom and his report intact.
    void navigate(location.pathname, {
      state: { ...(location.state as object | null), [REPORT_VIEWER_STATE_KEY]: key },
    });
  };
  const brandingConfigured = Boolean(branding && branding.company_name.trim().length > 0);

  // **EVERY HOOK ABOVE RUNS ON EVERY RENDER.** These two branches are the last
  // thing that happens, so an empty builder or an unresolved profile can never
  // change how many hooks were called.
  if (builder.captureIds.size === 0) {
    return <Navigate to={reportBuilderPath(builder.projectId)} replace />;
  }

  if (waiting) {
    return (
      <main className="cl-screen report-screen">
        <p className="projects-loading" role="status">
          Preparing your report…
        </p>
      </main>
    );
  }

  return (
    <main className="cl-screen report-screen">
      <p className="project-home-back">
        <button
          type="button"
          className="cl-button cl-button--quiet report-back-button"
          onClick={() => {
            void navigate(-1);
          }}
        >
          ‹ Photos
        </button>
      </p>

      {/* A quiet label rather than a headline: the report is the content, and
          the app's own title must not compete with the company name on it. */}
      <h1 className="report-preview-label">Report preview</h1>

      <label className="report-branding-toggle">
        <input
          type="checkbox"
          checked={builder.includeBranding}
          onChange={builder.toggleBranding}
          disabled={!brandingConfigured}
        />
        <span>Include company branding</span>
      </label>

      {/* A quiet pointer, never a wizard and never a block: an unbranded report
          is a perfectly good report. */}
      {!brandingConfigured ? (
        <p className="report-lead">
          Add your company details in <Link to={SETTINGS_PATH}>Settings</Link> to put them on your
          reports.
        </p>
      ) : null}

      {/* **ONLY THE REPORT IS ZOOMED.** Back, the label and the branding
          checkbox live outside this surface and stay ordinary Android size.

          **AT DEFAULT SCALE THIS IS NOT A WINDOW AT ALL** — the document is in
          ordinary page flow, so one finger scrolls the whole report and the
          line below it comes after the last photo because it comes after it in
          the document. It becomes a window only while zoomed, so that a drag
          pans the enlarged document instead of fighting the page's scrolling. */}
      <div
        className={`report-zoom ${zoom.isZoomed ? 'report-zoom--zoomed' : 'report-zoom--fit'}`}
        ref={measureSurface}
        {...zoom.handlers}
      >
        <div
          className="report-zoom-inner"
          style={{
            transform: `translate(${zoom.state.pan.x}px, ${zoom.state.pan.y}px) scale(${zoom.state.zoom})`,
            transformOrigin: '0 0',
          }}
        >
          {/* THE WHOLE REPORT, RESPONSIVE, IN ONE CONTINUOUS FLOW. No page
              boxes, no fixed height, no fit-to-width transform — those are what
              made the text tiny and clipped the later Captures. */}
          <article className="report-doc" ref={measureContent} aria-label="Report preview">
            <ReportDocumentBody document={document} onInspectPhoto={inspect} />
          </article>
        </div>
      </div>

      {/* The existing full-screen viewer, reused unchanged. It carries no
          Remove and no Before/After control, so inspecting evidence from a
          report can never alter it. */}
      {viewed ? (
        <PhotoViewer
          view={viewed}
          label={viewerLabel(viewed)}
          onClose={() => {
            // Back through the history entry the viewer occupies, so Android's
            // Back button and the Close button do exactly the same thing — and
            // neither leaves the preview.
            void navigate(-1);
          }}
        />
      ) : null}

      <ReportPdfActions pdf={pdf} />

      {/* Truthful boundary, narrowed by S10-04: the PDF and sharing exist now;
          KEEPING a generated report is S10-05 and is not built. */}
      <p className="projects-empty-body report-next-boundary">
        Saving reports so you can reopen and re-share them comes next. Nothing here has been saved.
      </p>
    </main>
  );
}
